import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { PagedData } from '../models/general.models';
import { Permission, Role, TeamMember } from '../models/team.model';
import { User } from '../models/user.model';
import { BaseApiService } from './base-api.service';
import { TokenResponse } from './interceptors/auth-interceptor.service';
import { CustomKeycloakService } from './keycloak.service';

@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseApiService {
  public currentUser: User;
  public mySlug: string;
  public authUser: any;

  constructor(
    http: HttpClient,
    router: Router,
    protected override keycloakService: CustomKeycloakService
  ) {
    super(http, router);

    this.initializeUserTokens();

    this.currentUser = JSON.parse(localStorage.getItem('user')) as User;
    this.mySlug = this.currentUser?.teams[0]?.slug;
  }

  private initializeUserTokens(): void {
    try {
      const keycloakToken = this.keycloakService.getStoredAccessToken();
      if (keycloakToken) {
        this.currentAccessToken = keycloakToken;
        this.currentRefreshToken = this.keycloakService.getStoredRefreshToken();
        return;
      }
    } catch (error) {
      console.warn('UserService: Error checking Keycloak tokens:', error);
    }

    this.currentAccessToken = localStorage.getItem('access_token');
    this.currentRefreshToken = localStorage.getItem('refresh_token');
  }

  public clearAuthData() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');

    this.keycloakService.clearStoredTokens();

    this.currentUser = null;
    this.currentAccessToken = null;
    this.currentRefreshToken = null;
    this.mySlug = null;

    if (this.keycloakService.isAuthenticated()) {
      this.keycloakService.logout();
    }
    window.open('https://authenticate.skyforest.io/.pomerium/sign_out?return_url=https://testing-app.skyforest.io/');
  }

  public getUser(): User {
    if (!this.currentUser?.id) {
      this.currentUser = JSON.parse(localStorage.getItem('user')) as User;
    }
    return this.currentUser;
  }

  public setUserData(token: TokenResponse) {
    this.setTokenPair(token);
    this.currentUser = token.user;
    this.mySlug = token.user?.teams[0].slug;
    localStorage.setItem('user', JSON.stringify(token.user));
  }
  sendInvite(): Observable<any> {
    return new Observable<any>((observer) => {
      observer.complete();
    });
  }

  getTeamUsers(page: number, limit: number): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/users?skip=${(page - 1) * limit}&limit=${limit}`
    );
  }

  getInvitedUsers(): Observable<any> {
    return new Observable<any>((observer) => {
      observer.complete();
    });
  }

  revokeInvitedUser(): Observable<any> {
    return new Observable<any>((observer) => {
      observer.complete();
    });
  }

  searchTeamUsers(query: string): Observable<Array<User>> {
    return this.http.get<Array<User>>(`${this.baseUrl}/users/?filters=email:like:%${query}%`);
  }

  updateTeamUser(): Observable<TeamMember> {
    return new Observable<TeamMember>((observer) => {
      observer.complete();
    });
  }

  removeTeamUser(): Observable<any> {
    return new Observable<any>((observer) => {
      observer.complete();
    });
  }

  getTeamRoles(): Observable<PagedData<Role>> {
    return new Observable<PagedData<Role>>((observer) => {
      observer.complete();
    });
  }

  createRole(): Observable<any> {
    return new Observable((observer) => {
      observer.complete();
    });
  }

  getPermissions(): Observable<PagedData<Permission>> {
    return new Observable<PagedData<Permission>>((observer) => {
      observer.complete();
    });
  }

  public getCurrentUser(): Observable<User> {
    const endpoint = `${this.baseUrl}/users/me`;
    return this.http.get<User>(endpoint);
  }

  public getUserById(id: string): Observable<User> {
    const endpoint = `${this.baseUrl}/users/${id}`;
    return this.http.get<User>(endpoint);
  }
}
